/* eslint-disable no-nested-ternary */
import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { FactorAppliesToLinkValue } from 'constantsProject';
import type {
    BimContractOtherCostItemData,
    ConstructionTypeData,
    ContractOtherCostItemData,
    ContractSeasonData,
    ProjectMethodologyEnum,
} from 'gql/graphql';
import {
    OtherCostCalculationTypeEnum,
    ContractOtherStatusEnum,
    ContractOtherConstructionTypeRelationStatusEnum,
} from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { formatNumberToRu, yesOrNoRepresentation } from 'utils';

import type { ColDef } from 'ag-grid-community';
import type { ColGroupDef } from 'ag-grid-community/dist/types/core/entities/colDef';
import { useCallback, useMemo } from 'react';

import { cellClass, MenuCellRenderer, notSubHeaderRow } from '../components/MenuCellRenderer';
import { NameCellRenderer } from '../components/NameCellRenderer';
import {
    uerContractOtherCostCalculatingTypeFilter,
    uerContractOtherCostNameFilter,
    uerContractOtherCostStatusFilter,
    uerContractOtherRelationStatusFilter,
} from '../contractOthersQueries';

export const ContractOtherStatusTitles = {
    [ContractOtherStatusEnum.AdditionalCondition]: 'Условие ДС',
    [ContractOtherStatusEnum.ContractCondition]: 'Условие договора',
    [ContractOtherStatusEnum.NewRecord]: 'Новая запись',
    [ContractOtherStatusEnum.Draft]: 'Черновик',
};

export const RelationStatusTitles = {
    [ContractOtherConstructionTypeRelationStatusEnum.AdditionalCondition]: 'Условие ДС',
    [ContractOtherConstructionTypeRelationStatusEnum.ContractCondition]: 'Условие договора',
    [ContractOtherConstructionTypeRelationStatusEnum.NewRecord]: 'Новая связь',
};

interface IUseContractOtherCostsColumns {
    seasons?: ContractSeasonData[] | null;
    costRounding: number;
    isUer: boolean;
    constructionTypes: ConstructionTypeData[];
    contractId: number;
    constructionTypeId: number;
    onDeleteSuccess: (id?: number) => void;
    hasWorkOrders: boolean;
    methodology: ProjectMethodologyEnum;
    changeFilterHandler: (params: { fieldName: string; checkedValues: string[] }) => void;
}

export const useContractOtherCostsColumns = (props: IUseContractOtherCostsColumns) => {
    const {
        seasons,
        costRounding,
        isUer,
        constructionTypes,
        contractId,
        constructionTypeId,
        hasWorkOrders,
        methodology,
        onDeleteSuccess,
        changeFilterHandler,
    } = props;

    const filterVariables = useMemo(
        () => ({
            contractId,
            constructionTypeId,
        }),
        [constructionTypeId, contractId],
    );

    const seasonsColumns = useMemo<ColDef[]>(
        () =>
            (seasons ?? []).map((season: ContractSeasonData) => ({
                headerName: `Сезон ${season.year}`,
                flex: 1,
                minWidth: 160,
                valueGetter: ({ data }) =>
                    notSubHeaderRow(data) ? formatNumberToRu(data[`season${season.id}`], costRounding) : '',
                cellClass,
            })),
        [costRounding, seasons],
    );

    const columns = useMemo<(ColDef | ColGroupDef)[]>(
        () => [
            {
                headerName: '',
                field: 'menu',
                lockPosition: 'left',
                pinned: 'left',
                cellRenderer: MenuCellRenderer,
                cellRendererParams: {
                    constructionTypes,
                    contractId,
                    constructionTypeId,
                    onOperationSuccess: onDeleteSuccess,
                    hasWorkOrders,
                    methodology,
                    costRounding,
                },
                width: 75,
                cellClass,
                resizable: false,
            },

            ...(isUer
                ? [
                      {
                          headerName: 'Наименование прочих затрат',
                          field: 'name',
                          minWidth: 448,
                          flex: 1,
                          cellClass,
                          cellRenderer: NameCellRenderer,
                          filter: AgGridCustomFilterAdapter,
                          filterParams: {
                              withSearch: false,
                              hideTableHeader: true,
                              variables: filterVariables,
                              dataAccessor: 'uerContractOtherCostNameFilter',
                              query: uerContractOtherCostNameFilter,
                              childFieldAccessor: 'value',
                              whereVariables: 'value',
                              columns: [
                                  {
                                      headerName: '',
                                      field: 'value',
                                      checkboxSelection: true,
                                      lockVisible: true,
                                      flex: 1,
                                  },
                              ],
                              changeFilterHandler,
                          },
                      },
                  ]
                : [
                      {
                          headerName: 'Наименование прочих затрат',
                          field: 'name',
                          minWidth: 448,
                          flex: 1,
                          cellClass,
                          cellRenderer: NameCellRenderer,
                      },
                  ]),
            ...(isUer
                ? [
                      {
                          headerName: 'Способ расчета',
                          field: 'calculatingType',
                          minWidth: 114,
                          flex: 1,
                          cellClass,
                          valueGetter: ({ data }: { data: ContractOtherCostItemData }) =>
                              data.calculatingType === OtherCostCalculationTypeEnum.Calculation
                                  ? 'Расчет'
                                  : data.calculatingTypeValue
                                  ? `${Number(data.calculatingTypeValue).toFixed(2)}%`
                                  : '',
                          filter: AgGridCustomFilterAdapter,
                          filterParams: {
                              withSearch: false,
                              hideTableHeader: true,
                              variables: filterVariables,
                              dataAccessor: 'uerContractOtherCostCalculatingTypeFilter',
                              query: uerContractOtherCostCalculatingTypeFilter,
                              childFieldAccessor: 'value',
                              whereVariables: 'value',
                              columns: [
                                  {
                                      headerName: '',
                                      field: 'value',
                                      checkboxSelection: true,
                                      lockVisible: true,
                                      flex: 1,
                                      valueFormatter: ({ value }: { value: OtherCostCalculationTypeEnum }) =>
                                          value === OtherCostCalculationTypeEnum.Calculation ? 'Расчет' : 'Процент',
                                  },
                              ],
                              changeFilterHandler,
                          },
                      },
                  ]
                : [
                      {
                          headerName: 'Способ расчета',
                          field: 'calculatingType',
                          minWidth: 112,
                          flex: 1,
                          cellClass,
                          valueGetter: ({ data }: { data: ContractOtherCostItemData }) =>
                              data.calculatingType === OtherCostCalculationTypeEnum.Calculation
                                  ? 'Расчет'
                                  : data.calculatingTypeValue
                                  ? `${Number(data.calculatingTypeValue).toFixed(3)}%`
                                  : '',
                      },
                  ]),

            ...(!isUer
                ? [
                      {
                          headerName: 'Лимиты по прочим',
                          field: 'cost',
                          valueGetter: ({ data }: { data: BimContractOtherCostItemData }) =>
                              formatNumberToRu(data.cost, 0),
                          cellClass,
                          minWidth: 160,
                          flex: 1,
                      },
                  ]
                : []),
            ...(isUer ? seasonsColumns : []),
            ...(isUer
                ? [
                      {
                          headerName: 'Статус записи',
                          field: 'status',
                          valueGetter: ({ data }: { data: ContractOtherCostItemData }) =>
                              notSubHeaderRow(data) ? ContractOtherStatusTitles[data.status] || '' : '',
                          minWidth: 160,
                          flex: 1,
                          cellClass,
                          filter: AgGridCustomFilterAdapter,
                          filterParams: {
                              withSearch: false,
                              hideTableHeader: true,
                              variables: filterVariables,
                              dataAccessor: 'uerContractOtherCostStatusFilter',
                              query: uerContractOtherCostStatusFilter,
                              childFieldAccessor: 'value',
                              whereVariables: 'value',
                              columns: [
                                  {
                                      headerName: '',
                                      field: 'value',
                                      checkboxSelection: true,
                                      lockVisible: true,
                                      flex: 1,
                                      valueFormatter: ({ value }: { value: ContractOtherStatusEnum }) =>
                                          FactorAppliesToLinkValue[value],
                                  },
                              ],
                              changeFilterHandler,
                          },
                      },
                  ]
                : [
                      {
                          headerName: 'Статус записи',
                          field: 'status',
                          valueGetter: ({ data }: { data: ContractOtherCostItemData }) =>
                              notSubHeaderRow(data) ? ContractOtherStatusTitles[data.status] || '' : '',
                          minWidth: 160,
                          flex: 1,
                          cellClass,
                      },
                  ]),
            ...(isUer
                ? [
                      {
                          headerName: 'Статус связи',
                          field: 'constructionTypeLimits?.[0].relationStatus',
                          valueGetter: ({ data }: { data: ContractOtherCostItemData }) =>
                              data?.constructionTypeLimits?.length
                                  ? RelationStatusTitles[data?.constructionTypeLimits?.[0].relationStatus]
                                  : '',
                          cellClass,
                          minWidth: 160,
                          flex: 1,
                          filter: AgGridCustomFilterAdapter,
                          filterParams: {
                              withSearch: false,
                              hideTableHeader: true,
                              variables: filterVariables,
                              dataAccessor: 'uerContractOtherRelationStatusFilter',
                              query: uerContractOtherRelationStatusFilter,
                              childFieldAccessor: 'value',
                              whereVariables: 'value',
                              columns: [
                                  {
                                      headerName: '',
                                      field: 'value',
                                      checkboxSelection: true,
                                      lockVisible: true,
                                      flex: 1,
                                      valueFormatter: ({
                                          value,
                                      }: {
                                          value: ContractOtherConstructionTypeRelationStatusEnum;
                                      }) => RelationStatusTitles[value],
                                  },
                              ],
                              changeFilterHandler,
                          },
                      },
                  ]
                : []),
            ...(isUer
                ? [
                      {
                          headerName: 'Код',
                          field: 'key',
                          cellClass,
                          minWidth: 160,
                          flex: 1,
                      },
                  ]
                : []),
            {
                headerName: 'Используется',
                minWidth: 260,
                flex: 1,
                children: [
                    {
                        headerName: 'НЗ',
                        field: 'isIncludedInWorkOrder',
                        valueGetter: ({ data }: { data: ContractOtherCostItemData }) =>
                            notSubHeaderRow(data) ? yesOrNoRepresentation(data.isIncludedInWorkOrder) : '',
                        minWidth: 90,
                        flex: 1,
                        cellClass,
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        headerName: 'Акт',
                        field: 'isIncludedInActKc2',
                        valueGetter: ({ data }: { data: ContractOtherCostItemData }) =>
                            notSubHeaderRow(data) ? yesOrNoRepresentation(data.isIncludedInActKc2) : '',
                        minWidth: 90,
                        flex: 1,
                        cellClass,
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        headerName: 'Изм. к НЗ',
                        field: 'isIncludedInWorkOrderChange',
                        valueGetter: ({ data }: { data: ContractOtherCostItemData }) =>
                            notSubHeaderRow(data) ? yesOrNoRepresentation(data.isIncludedInWorkOrderChange) : '',
                        minWidth: 100,
                        flex: 1,
                        cellClass,
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
        ],
        [
            changeFilterHandler,
            constructionTypeId,
            constructionTypes,
            contractId,
            costRounding,
            filterVariables,
            hasWorkOrders,
            isUer,
            methodology,
            onDeleteSuccess,
            seasonsColumns,
        ],
    );

    const [selection, , , setSelection] = useSelection('contractOtherCosts');
    const [pinnedItems, , , setPinned] = usePin('contractOtherCosts');

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };
                    const columnId = getColumnId(column);

                    if (idx < 2) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[columnId]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setSelection, setPinned],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
    };
};
